import { useEffect, useRef, useState } from "react";
import { resolveApiUrl } from "views_shared/app/javascript/api/api";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import qlog from "views_shared/app/javascript/scripts/qlog";

// One whole-profile vector per model. Standard (per-content) embeddings are deliberately not
// offered here: the backend applies its stored-vector type filter asymmetrically (only the
// aggregated class gets a type predicate), so a "standard" option would not mean what it says.
// A consumer whose model has no aggregated embedding (e.g. Media Outlet) extends this then.
const AGGREGATED_EMBEDDING = "MatchFeed::AggregatedEmbedding";

/**
 * useViewersAlsoView — data half of the shared "Viewers also view" rail card.
 *
 * Asks the similarity API for the nearest neighbors of the profile being viewed
 * (model-as-query over its stored embedding — no OpenAI call is made) and hydrates
 * the resulting ids through an engine-supplied adapter. The similarity API is
 * anonymous-open and returns only `(model_type, model_id, distance)` — no display
 * data and no per-viewer visibility — so the hydrate adapter MUST read display rows
 * through the engine's PostgREST view: its RLS silently drops profiles this viewer
 * isn't entitled to see, and the hook keeps whatever survives, in similarity order.
 *
 * Never throws and never reports upward: a rail filler must not take down the page,
 * so any failure resolves to an empty item list. (A profile with no embedding yet is
 * not a failure — the API returns a normal response with empty results for it.)
 *
 * @param {Object} options
 * @param {string} options.queryModelType - Rails class name of the profile being viewed
 *   (e.g. "Id::Profile::Promoter"); opaque to this hook, forwarded to the API
 * @param {number|string} [options.queryModelId] - Numeric id of the profile being viewed
 *   (NOT a slug). The hook idles until this is set, so pass the id from the loaded row
 * @param {Function} options.hydrate - `async (ids, neighbors) => [{ id, modelType?, href, imageUrl,
 *   title, subtitle }]` — the engine adapter. `ids` are the neighbor model ids (similarity order);
 *   `neighbors` are the raw result objects for adapters that need `model_type`. May resolve to fewer
 *   rows than ids (RLS dropouts); order need not be preserved — the hook re-orders by similarity.
 *   When searching MULTIPLE model types, every returned row must carry `modelType` (numeric ids
 *   collide across models — rows without one can't be matched back and are dropped). Single-type
 *   searches may omit it. Read via a ref, so an inline function is fine
 * @param {string[]} [options.searchModelTypes] - Result model types (default: same type as the query)
 * @param {Array<number|string>} [options.excludeIds] - Result model ids to drop regardless of type
 *   (e.g. profiles already shown elsewhere on the page). The viewed profile itself is always dropped
 * @param {number} [options.maxItems] - Display cap (the fetch asks for extra as dropout headroom)
 * @returns {{ items: Array, isLoading: boolean }}
 */
function useViewersAlsoView({
  queryModelType,
  queryModelId = null,
  hydrate,
  searchModelTypes = null,
  excludeIds = [],
  maxItems = 5,
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Refs + joined keys keep the effect's dependency list primitive, so callers may pass
  // inline functions/arrays without memoizing them (and without re-fetch loops).
  const hydrateRef = useRef(hydrate);
  hydrateRef.current = hydrate;
  const excludeKey = excludeIds.map(String).join(",");
  const searchTypesKey = (searchModelTypes || [queryModelType]).join(",");

  useEffect(() => {
    if (!queryModelId) return undefined;

    let componentIsMounted = true;

    const load = async () => {
      try {
        setIsLoading(true);

        const params = new URLSearchParams({
          query_model_type: queryModelType,
          query_model_id: String(queryModelId),
          query_model_embedding_type: AGGREGATED_EMBEDDING,
          // Also constrain the RESULT rows to the aggregated family — profiles carry both a
          // standard and an aggregated embedding, and without this filter each neighbor could
          // appear twice.
          embedding_types: AGGREGATED_EMBEDDING,
          search_model_types: searchTypesKey,
          limit: String(maxItems * 2 + 2),
        });

        // The similarity API sits behind TokenAuthentication: "anonymous-open" means no USER
        // is required, but every caller must still present the app's anon apikey header
        // (authenticatedHeadersHash supplies it, plus the user's bearer when logged in).
        // V1-COMPAT: resolveApiUrl no-ops in V2; when this file is copied verbatim into V1 it
        // crosses this Rails API call over to V2's origin instead of hitting V1 same-origin.
        const response = await fetch(resolveApiUrl(`/match_feed/api/similarity_search?${params.toString()}`), {
          headers: new Headers(authenticatedHeadersHash()),
        });
        if (!response.ok) {
          // Any HTTP error is non-fatal here — the rail just stays empty.
          if (componentIsMounted) setItems([]);
          return;
        }
        const body = await response.json();
        const results = body?.data?.results || [];

        const excluded = new Set(excludeKey ? excludeKey.split(",") : []);
        const seen = new Set();
        const neighbors = [];
        results.forEach((result) => {
          const id = String(result.model_id);
          // The query profile is its own nearest neighbor (distance 0) — always drop it.
          if (result.model_type === queryModelType && id === String(queryModelId)) return;
          if (excluded.has(id)) return;
          const key = `${result.model_type}:${id}`;
          if (seen.has(key)) return;
          seen.add(key);
          neighbors.push(result);
        });

        if (!neighbors.length) {
          if (componentIsMounted) setItems([]);
          return;
        }

        const neighborIds = neighbors.map((neighbor) => neighbor.model_id);
        const rows = (await hydrateRef.current(neighborIds, neighbors)) || [];
        // Re-impose similarity order, matching rows back by (model type, id) — numeric ids collide
        // across model types, so an id-only key could render the wrong row on multi-type searches.
        // Rows without a modelType default to the single searched type; on a multi-type search an
        // untyped row is ambiguous and dropped (the hydrate contract requires typing them).
        const searchTypes = searchTypesKey.split(",");
        const defaultRowType = searchTypes.length === 1 ? searchTypes[0] : null;
        const rowsByKey = new Map(
          rows
            .map((row) => [row.modelType || defaultRowType, row])
            .filter(([type]) => type)
            .map(([type, row]) => [`${type}:${String(row.id)}`, row]),
        );
        const ordered = neighbors
          .map((neighbor) => rowsByKey.get(`${neighbor.model_type}:${String(neighbor.model_id)}`))
          .filter(Boolean)
          .slice(0, maxItems);

        if (componentIsMounted) setItems(ordered);
      } catch (error) {
        qlog.error("ViewersAlsoView similarity lookup failed:", error);
        if (componentIsMounted) setItems([]);
      } finally {
        if (componentIsMounted) setIsLoading(false);
      }
    };
    load();

    return () => {
      componentIsMounted = false;
    };
  }, [queryModelType, queryModelId, searchTypesKey, excludeKey, maxItems]);

  return { items, isLoading };
}

export default useViewersAlsoView;
